/**
 * Live schedule — PURE due-date math for the per-agent "liveSchedule" flag.
 *
 * The global autonomy switch (agentScheduler.ts) is opt-in and off by
 * default, which meant nothing ever ticked for most operators. The live
 * schedule closes that gap SAFELY: while the app is open, a 60s ticker
 * (src/lib/agent/useLiveSchedule.ts) evaluates these pure predicates and
 * runs any due agent whose own `liveSchedule` flag is on — independent of
 * the global toggle. Safe-by-design because a run only ever QUEUES
 * proposals into the approvals inbox; it never writes CRM data directly.
 *
 * This module is deliberately dependency-free (types only) so the due
 * evaluation is unit-testable and so `db/agents.ts` can import
 * `nextLiveRunIso` without an import cycle (the ticker lives in a
 * separate module that imports from both).
 */

import type { AgentCadence, DeployedAgent } from '../db/types';

/**
 * Cadence → minimum gap between live runs, in ms. `manual` is intentionally
 * absent — a manual agent never ticks. `continuous` matches the global
 * scheduler's demo-visible throttle, which on a 60s ticker means "every
 * tick".
 */
export const LIVE_CADENCE_MS: Partial<Record<AgentCadence, number>> = {
  continuous: 30_000,
  hourly: 60 * 60_000,
  daily: 24 * 60 * 60_000,
};

/** The slice of a DeployedAgent the due evaluation actually reads. */
export type LiveScheduleFields = Pick<
  DeployedAgent,
  'cadence' | 'lastRunAt' | 'state' | 'liveSchedule'
>;

/**
 * PURE: cadence + lastRun + now → due? An agent that has never run is due
 * immediately; a `manual` cadence is never due. This deliberately ignores
 * the liveSchedule flag / lifecycle state so it can be tested in isolation
 * — `isLiveEligible` layers those on.
 */
export function isLiveDue(
  cadence: AgentCadence,
  lastRunAt: string | null | undefined,
  now = Date.now(),
): boolean {
  const gap = LIVE_CADENCE_MS[cadence];
  if (gap == null) return false; // manual — never auto-runs
  const last = lastRunAt ? new Date(lastRunAt).getTime() : 0;
  return now >= last + gap;
}

/**
 * Is this agent even a candidate for live ticking? Requires the per-agent
 * flag ON and a non-manual cadence. Paused agents don't tick (pausing is
 * the operator's stop button) — but `idle` ones DO: non-continuous agents
 * settle to `idle` after every run, and requiring `running` (like the
 * global scheduler does) would let them tick exactly once, ever.
 */
export function isLiveEligible(agent: LiveScheduleFields): boolean {
  return (
    agent.liveSchedule === true &&
    agent.cadence !== 'manual' &&
    agent.state !== 'paused'
  );
}

/** Eligible AND overdue right now. */
export function shouldLiveRun(agent: LiveScheduleFields, now = Date.now()): boolean {
  return isLiveEligible(agent) && isLiveDue(agent.cadence, agent.lastRunAt, now);
}

/**
 * The due subset of `agents`, most-overdue first (never-ran sorts ahead of
 * recently-ran), so the ticker drains the backlog in a fair order.
 */
export function dueLiveAgents(agents: DeployedAgent[], now = Date.now()): DeployedAgent[] {
  return agents
    .filter((a) => shouldLiveRun(a, now))
    .sort((a, b) => {
      const at = a.lastRunAt ? new Date(a.lastRunAt).getTime() : 0;
      const bt = b.lastRunAt ? new Date(b.lastRunAt).getTime() : 0;
      return at - bt;
    });
}

/**
 * ISO timestamp of the next run implied by `cadence` after a run at
 * `lastRunAt`; null for `manual` (never auto-runs). Persisted onto the
 * agent record by `runAgentOnce` so cards can show a live "next run".
 */
export function nextLiveRunIso(
  cadence: AgentCadence,
  lastRunAt: string,
): string | null {
  const gap = LIVE_CADENCE_MS[cadence];
  if (gap == null) return null;
  return new Date(new Date(lastRunAt).getTime() + gap).toISOString();
}
